import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Container,
  CircularProgress,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Button,
  Fab,
  Popover,
  styled,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import axios from "axios";
import TopToolbar from "./TopToolbar";
import Footer from "./Footer";
import { jsonToHtml } from "./utils/tiptapConfig";
import { useSelector } from "react-redux";

// ---------------- STYLED COMPONENTS ----------------

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fb",
  backgroundImage:
    "radial-gradient(circle at 15% 0%, rgba(54,162,235,0.06) 0%, transparent 45%), radial-gradient(circle at 100% 20%, rgba(75,192,192,0.06) 0%, transparent 40%)",
}));

// Sidebar offset steps down through breakpoints instead of jumping straight
// from desktop width to 0, so the content column never feels squeezed.
const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "60px" : "220px",
  minWidth: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("md")]: {
    marginLeft: $collapsed ? "60px" : "0px",
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const MobileToggleButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  left: 16,
  top: 16,
  zIndex: theme.zIndex.drawer + 2,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.dark,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
  boxShadow: theme.shadows[4],
  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
}));

const GlassPanel = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: "rgba(255,255,255,0.85)",
  border: "1px solid rgba(255,255,255,0.4)",
  backdropFilter: "blur(6px)",
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "4px",
    height: "100%",
    background: "linear-gradient(to bottom, #36a2eb, #4bc0c0)",
  },
}));

const ArticleDetailPage = () => {
  // Hooks and state declarations
  const navigate = useNavigate();
  const { articleId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery("(max-width:600px)");

  const [article, setArticle] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSpecificCategory, setShowSpecificCategory] = useState(false);
  const [relatedAnchorEl, setRelatedAnchorEl] = useState(null);

  const { articles = [] } = useSelector((state) => ({
    articles: state.support.filteredArticles || [],
  }));

  // Helper functions
  const findArticleInRedux = (id) => {
    return articles.find((item) => (item._id || item.id) === id);
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarExpanded(!sidebarExpanded);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleArticleClick = (clickedArticle) => {
    navigate(`/articles/${clickedArticle._id || clickedArticle.id}`);
  };

  const handleRelatedClick = (event) => {
    if (relatedArticles.length === 0) return;
    setRelatedAnchorEl(event.currentTarget);
  };

  const handleRelatedClose = () => {
    setRelatedAnchorEl(null);
  };

  // Effects
  useEffect(() => {
    if (isMobile) {
      setShowSpecificCategory(true);
      setMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `/api/support/view-single-article/${articleId}`
        );
        const fetchedArticle = response.data.article;
        const reduxArticle = findArticleInRedux(articleId);
        if (fetchedArticle || reduxArticle) {
          const formattedArticle = {
            ...fetchedArticle,
            ...reduxArticle,
            title:
              reduxArticle?.title ||
              fetchedArticle?.title ||
              "Untitled Article",
            createdBy:
              reduxArticle?.createdBy ||
              fetchedArticle?.createdBy ||
              "Unknown Author",
            createdOn:
              reduxArticle?.createdOn ||
              fetchedArticle?.createdOn ||
              new Date().toISOString(),
            categoryName:
              reduxArticle?.categoryName ||
              fetchedArticle?.categoryName ||
              "Uncategorized",
            content: fetchedArticle?.content || fetchedArticle,
          };

          setArticle(formattedArticle);
          const html = jsonToHtml(formattedArticle.content);
          setHtmlContent(html);

          if (formattedArticle.categoryName) {
            const related = articles.filter(
              (item) =>
                item.categoryName === formattedArticle.categoryName &&
                (item._id || item.id) !==
                  (formattedArticle._id || formattedArticle.id)
            );
            setRelatedArticles(related);
          }
        }
      } catch (error) {
        console.error("Error fetching article:", error);
        const reduxArticle = findArticleInRedux(articleId);
        if (reduxArticle) {
          setArticle(reduxArticle);
          const html = jsonToHtml(reduxArticle.content || reduxArticle);
          setHtmlContent(html);

          if (reduxArticle.categoryName) {
            const related = articles.filter(
              (item) =>
                item.categoryName === reduxArticle.categoryName &&
                (item._id || item.id) !== (reduxArticle._id || reduxArticle.id)
            );
            setRelatedArticles(related);
          }
        } else {
          setArticle(null);
          setHtmlContent("");
          setRelatedArticles([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId, articles]);

  if (isMobile && !mobileOpen && !showSpecificCategory) {
    return (
      <MobileToggleButton onClick={toggleSidebar}>
        <MenuIcon />
      </MobileToggleButton>
    );
  }

  return (
    <MainContainer>
      {isMobile && (
        <MobileToggleButton
          onClick={toggleSidebar}
          sx={{ left: mobileOpen ? 240 : 16 }}
        >
          {mobileOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </MobileToggleButton>
      )}

      <TopToolbar
        expanded={sidebarExpanded}
        toggleExpand={toggleSidebar}
        mobileOpen={mobileOpen}
      />

      <MainContent $collapsed={!sidebarExpanded && !isMobile}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            gap: { xs: 2.5, md: 3.5 },
            position: "relative",
            zIndex: 1,
            pt: 1,
            pb: { xs: 12, sm: 14 },
            px: 0,
            maxWidth: { xs: "100%", md: "1400px", lg: "1600px" },
            width: "100%",
            margin: "0 auto",
          }}
        >
          {/* Article Content - Left Side */}
          <Box
            sx={{
              width: "100%",
              flex: 1,
              minWidth: 0,
              bgcolor: "background.paper",
              borderRadius: { xs: 3, md: 4 },
              boxShadow: "0 12px 40px -12px rgba(0,0,0,0.15)",
              p: isSmallMobile ? 2.5 : isMobile ? 3 : { md: 4, lg: 5 },
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.9)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              minHeight: { xs: "auto", md: "72vh", lg: "78vh" },
              "&:after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #36a2eb, #4bc0c0)",
                zIndex: 2,
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                  minHeight: "40vh",
                }}
              >
                <CircularProgress size={56} thickness={4} />
              </Box>
            ) : !article || !htmlContent ? (
              <Box sx={{ width: "100%", textAlign: "center", py: 8 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  Article not found
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleClose}
                  sx={{ mt: 3, borderRadius: "999px", textTransform: "none" }}
                >
                  Go back
                </Button>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    right: isSmallMobile ? 8 : 16,
                    top: isSmallMobile ? 8 : 16,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    zIndex: 3,
                  }}
                >
                  <IconButton
                    aria-label="back"
                    onClick={handleClose}
                    sx={{
                      color: theme.palette.grey[700],
                      backgroundColor: "rgba(0,0,0,0.04)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.09)",
                        transform: "translateX(-4px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ArrowBack fontSize={isMobile ? "medium" : "large"} />
                  </IconButton>
                </Box>

                <Popover
                  open={Boolean(relatedAnchorEl)}
                  anchorEl={relatedAnchorEl}
                  onClose={handleRelatedClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      borderRadius: 3,
                      p: 1.5,
                      width: { xs: 280, sm: 320 },
                      bgcolor: "background.paper",
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "rgba(255,255,255,0.95)",
                      boxShadow: "0 12px 40px -12px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <LibraryBooksIcon fontSize="small" /> More in {article?.categoryName || "this category"}
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {relatedArticles.length > 0 ? (
                        relatedArticles.map((item) => (
                          <ListItem key={item._id || item.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                handleRelatedClose();
                                handleArticleClick(item);
                              }}
                            >
                              <ListItemText
                                primary={item.title || "Untitled Article"}
                                secondary={item.categoryName || "Uncategorized"}
                                primaryTypographyProps={{ fontWeight: 600 }}
                                secondaryTypographyProps={{ color: "text.secondary" }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                          No related articles
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Popover>

                <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      color: "primary.main",
                      pr: { xs: 5, sm: 6 },
                      fontSize: isSmallMobile
                        ? "1.6rem"
                        : isMobile
                        ? "2rem"
                        : { md: "2.3rem", lg: "2.6rem" },
                      lineHeight: 1.25,
                      mb: 3,
                      wordBreak: "break-word",
                      background: "linear-gradient(90deg, #1976d2, #4facfe)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {article.title}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1.25, sm: 2 },
                      mb: 4,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      label={article.categoryName}
                      color="primary"
                      size={isSmallMobile ? "small" : "medium"}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderWidth: "2px",
                        "& .MuiChip-label": {
                          px: 1.8,
                          fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                          letterSpacing: "0.2px",
                        },
                        borderRadius: 1,
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem", md: "1.05rem" } }}
                      >
                        By IPPIS
                      </Typography>
                      <Box
                        sx={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          opacity: 0.6,
                        }}
                      />
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.85rem", sm: "1rem", md: "1.05rem" } }}
                      >
                        {new Date(article.createdOn).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider
                    sx={{
                      my: 3,
                      borderColor: "divider",
                      borderBottomWidth: "2px",
                      background:
                        "linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.35), transparent)",
                      height: "2px",
                    }}
                  />

                  <Box sx={{ mb: 2, width: "100%" }}>
                    <Box
                      sx={{
                        // Base text scaling
                        fontSize: {
                          xs: "0.98rem",
                          sm: "1.04rem",
                          md: "1.1rem",
                          lg: "1.15rem",
                        },
                        lineHeight: { xs: 1.7, sm: 1.78, md: 1.85 },
                        color: "text.primary",
                        wordBreak: "break-word",

                        // Paragraphs and lists
                        "& p, & li": {
                          mb: 2,
                          fontSize: "inherit",
                          lineHeight: "inherit",
                        },
                        "& ul, & ol": {
                          pl: 3,
                          mb: 2,
                          "& li": { mb: 1 },
                        },

                        // Headings
                        "& h1": {
                          fontSize: isSmallMobile ? "1.6rem" : "2.2rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                          lineHeight: 1.25,
                        },
                        "& h2": {
                          fontSize: isSmallMobile ? "1.4rem" : "1.9rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          lineHeight: 1.3,
                        },
                        "& h3": {
                          fontSize: isSmallMobile ? "1.25rem" : "1.6rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          lineHeight: 1.35,
                        },

                        // Links
                        "& a": {
                          color: theme.palette.primary.main,
                          textDecorationColor: theme.palette.primary.main,
                          textDecorationThickness: "2px",
                          wordBreak: "break-word",
                        },

                        // Images
                        "& img": {
                          width: "100%",
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: 1,
                          boxShadow: theme.shadows[2],
                          display: "block",
                          margin: "1rem auto",
                          objectFit: "contain",
                        },
                        "& figure": {
                          m: 0,
                        },
                        "& figure img": {
                          width: "100%",
                          maxWidth: "100%",
                          height: "auto",
                          display: "block",
                        },
                        // Embedded media
                        "& iframe, & video": {
                          width: "100%",
                          height: { xs: "auto", md: "60vh" },
                          borderRadius: 1,
                          display: "block",
                          margin: "1rem auto",
                        },

                        // Tables
                        "& table": {
                          borderCollapse: "collapse",
                          width: "100%",
                          margin: "1rem 0",
                          display: "block",
                          overflowX: "auto",
                        },
                        "& th, & td": {
                          border: "1px solid #ddd",
                          padding: { xs: "8px", md: "12px" },
                          textAlign: "left",
                          fontSize: "inherit",
                        },
                        "& th": {
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },

                        // Code blocks
                        "& pre": {
                          overflowX: "auto",
                        },
                        "& pre, & code": {
                          fontSize: "0.9em",
                          backgroundColor: "rgba(0,0,0,0.04)",
                          borderRadius: 1,
                          padding: "0.25rem 0.5rem",
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* Related Articles Menu - Right Side on Desktop, Below Content on Mobile/Tablet */}
          {isTablet ? (
            relatedArticles.length > 0 && (
              <Box sx={{ width: "100%" }}>
                <GlassPanel elevation={3} sx={{ p: isSmallMobile ? 2 : 2.5, pl: isSmallMobile ? 2.5 : 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                    }}
                  >
                    <LibraryBooksIcon fontSize="small" />
                    {article ? `More in ${article.categoryName}` : "Related Articles"}
                  </Typography>

                  <List dense sx={{ py: 0 }}>
                    {relatedArticles.map((item) => (
                      <ListItem
                        key={item._id || item.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: "hidden",
                          bgcolor:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                          borderLeft:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "3px solid"
                              : "none",
                          borderColor:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "primary.main"
                              : "transparent",
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleArticleClick(item)}
                          selected={
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                          }
                          sx={{
                            py: isSmallMobile ? 1 : 1.5,
                            px: isSmallMobile ? 1 : 2,
                            "&.Mui-selected": {
                              bgcolor: "transparent",
                              "&:hover": {
                                bgcolor: "rgba(25, 118, 210, 0.12)",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.title || "Untitled Article"}
                            primaryTypographyProps={{
                              fontWeight:
                                (item._id || item.id) ===
                                (article?._id || article?.id)
                                  ? 600
                                  : 500,
                              color:
                                (item._id || item.id) ===
                                (article?._id || article?.id)
                                  ? "primary.main"
                                  : "text.primary",
                              noWrap: true,
                              textOverflow: "ellipsis",
                              fontSize: isSmallMobile ? "0.9rem" : "1rem",
                            }}
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                }}
                              >
                                {item.categoryName || "Uncategorized"}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </GlassPanel>
              </Box>
            )
          ) : (
            <Box
              sx={{
                width: { md: "300px", lg: "340px" },
                flexShrink: 0,
                position: "sticky",
                top: 16,
                alignSelf: "flex-start",
                maxHeight: "calc(100vh - 100px)",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                },
              }}
            >
              <GlassPanel elevation={3} sx={{ p: 2.5, pl: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: { md: "1.1rem", lg: "1.15rem" },
                    letterSpacing: "0.2px",
                  }}
                >
                  <LibraryBooksIcon fontSize="small" />
                  {article
                    ? `More in ${article.categoryName}`
                    : "Related Articles"}
                </Typography>

                <List dense sx={{ py: 0 }}>
                  {relatedArticles.length > 0 ? (
                    relatedArticles.map((item) => (
                      <ListItem
                        key={item._id || item.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: "hidden",
                          bgcolor:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                          borderLeft:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "3px solid"
                              : "none",
                          borderColor:
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                              ? "primary.main"
                              : "transparent",
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleArticleClick(item)}
                          selected={
                            (item._id || item.id) ===
                            (article?._id || article?.id)
                          }
                          sx={{
                            py: 1.5,
                            px: 2,
                            "&.Mui-selected": {
                              bgcolor: "transparent",
                              "&:hover": {
                                bgcolor: "rgba(25, 118, 210, 0.12)",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.title || "Untitled Article"}
                            primaryTypographyProps={{
                              fontWeight:
                                (item._id || item.id) ===
                                (article?._id || article?.id)
                                  ? 600
                                  : 500,
                              color:
                                (item._id || item.id) ===
                                (article?._id || article?.id)
                                  ? "primary.main"
                                  : "text.primary",
                              noWrap: true,
                              textOverflow: "ellipsis",
                              fontSize: { md: "1rem", lg: "1.05rem" },
                            }}
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  fontSize: { md: "0.85rem", lg: "0.9rem" },
                                }}
                              >
                                {item.categoryName || "Uncategorized"}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, py: 1.5 }}
                    >
                      No related articles found
                    </Typography>
                  )}
                </List>
              </GlassPanel>
            </Box>
          )}
        </Container>

      </MainContent>

      {/* GET SUPPORT Button — rendered as a direct child of MainContainer
          (not nested inside MainContent/Container) so no ancestor with
          padding, overflow, or a CSS transform can ever clip or shift it.
          zIndex is pinned above the drawer/app-bar so it's always on top,
          and it steps from an icon-only Fab on narrow screens to a full
          pill button once there's room for the label. */}
      <Fab
        color="primary"
        aria-label="get support"
        onClick={() => navigate("/CreateTicket")}
        sx={{
          position: "fixed",
          bottom: { xs: 20, sm: 24, md: 32 },
          right: { xs: 16, sm: 20, md: 32 },
          zIndex: (t) => t.zIndex.modal + 10,
          display: { xs: "inline-flex", md: "none" },
          boxShadow: "0 4px 14px rgba(25, 118, 210, 0.35)",
          "&:hover": {
            transform: "scale(1.08)",
            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.45)",
            bgcolor: "#22BB33",
          },
          transition: "all 0.3s ease",
        }}
      >
        <ChatBubbleIcon />
      </Fab>

      <Button
        variant="contained"
        color="primary"
        startIcon={<ChatBubbleIcon />}
        onClick={() => navigate("/CreateTicket")}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          zIndex: (t) => t.zIndex.modal + 10,
          display: { xs: "none", md: "inline-flex" },
          borderRadius: "999px",
          px: 3,
          py: 1.5,
          fontSize: "0.875rem",
          fontWeight: 600,
          textTransform: "none",
          whiteSpace: "nowrap",
          boxShadow: "0 4px 14px rgba(25, 118, 210, 0.35)",
          minWidth: "180px",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.45)",
            bgcolor: "primary.dark",
          },
          transition: "all 0.3s ease",
        }}
      >
        GET SUPPORT
      </Button>

      {/* Footer */}
      <Footer collapsed={!sidebarExpanded && !isMobile} />
    </MainContainer>
  );
};

export default ArticleDetailPage;